<script setup lang="ts">
import { cn } from '@pokerly/ui';
import { computed, onMounted, useTemplateRef } from 'vue';
import FaceDownCard from '@/components/FaceDownCard.vue';
import { useSessionStore } from '@/stores/session.ts';
import type { Participant } from '@/types/participant.js';
import UserAvatar from '@/components/user-avatar/UserAvatar.vue';
import { EmojiThrowableUserAvatar } from '@/components/emoji/index.js';
import type { EmojiData } from 'emoji-mart-vue-fast/src';
import { useParticipantLayout } from '@/composables/use-participant-layout.ts';
import { useEmojiThrowHandler } from '@/composables/use-emoji-throw-handler.ts';

interface Props {
    participants: Participant[];
}

const props = defineProps<Props>();
const boardRef = useTemplateRef('board');
const avatarsRef = useTemplateRef('avatars');
const sessionStore = useSessionStore();

// Use the participant layout composable for positioning
const { getParticipantPositionStyles, getCardFinalPositionStyles, updateBoardCenter } = useParticipantLayout({
    boardRef,
    participants: computed(() => props.participants),
});

// Use the emoji throw handler composable for interactions
const { handleEmojiThrow, setupInteractionListener } = useEmojiThrowHandler({
    avatarsRef,
});

onMounted(() => {
    updateBoardCenter();
    setupInteractionListener();
});
</script>

<template>
    <div class="p-22">
        <div
            ref="board"
            :class="
                cn(
                    'rounded-full',
                    'size-40',
                    'sm:size-50',
                    'md:size-60',
                    'lg:size-70',
                    'xl:size-70',
                    'm-auto',
                    'aspect-square',
                    'bg-surface-accent-1',
                    'border',
                    'border-border-border-1',
                    'shadow-[0_0_56px_0_hsla(0,0%,0%,0.2)]',
                    'bg-radial-[50%_50%_at_50%_50%]',
                    'from-[rgba(12,0,74,0.00)] from-0%',
                    'to-[rgba(12,0,74,0.10)] to-100%',
                    'bg-surface-accent-1',
                    'relative',
                )
            "
        >
            <template v-for="participant in participants" :key="participant.id">
                <div class="absolute -translate-[50%] z-10" :style="getParticipantPositionStyles(participant)">
                    <UserAvatar
                        v-if="sessionStore.isAuthenticatedUser(participant)"
                        ref="avatars"
                        :participant="participant"
                        size="lg"
                    />
                    <EmojiThrowableUserAvatar
                        v-else
                        ref="avatars"
                        :participant="participant"
                        @throw="(emoji: EmojiData, target: DOMRect) => handleEmojiThrow(emoji, target, participant.id)"
                        size="lg"
                    />
                </div>
                <FaceDownCard
                    :style="getCardFinalPositionStyles(participant)"
                    class="absolute translate-x-(--initial-position-x) translate-y-(--initial-position-y) data-[active=true]:translate-y-(--final-position-y) data-[active=true]:translate-x-(--final-position-x) scale-0"
                    :flipped="sessionStore.isRoundClosed"
                    :active="!!participant.vote"
                    :value="participant.vote"
                />
            </template>
        </div>
    </div>
</template>
